import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  amplitudeService,
  createTimeZoneOption,
  timeZoneMappingService,
  timeZonesService,
} from 'services';
import { SupportedTimeZones } from 'enums';

import type {
  TimeZoneSelectOption,
  TimeZonesMap,
  RemoteLevel,
  TimeZoneWithCountryData,
} from 'types';
import { levels } from 'consts';
import { getParentAppLocationObject, setParentSearchParams } from 'iframe';

const LEVEL_KEY = 'level';
const TZ_NAME_KEY = 'timezone';
// todo this was init implementation for custom remote level, might be needed in future
// const WORK_DAY_START_KEY = 'wdstart';
// const WORK_DAY_START_AMPM_KEY = 'wdstartampm';
// const WORK_DAY_END_KEY = 'wdend';
// const WORK_DAY_END_AMPM_KEY = 'wdendampm';
// const OVERLAP_KEY = 'overlap';

const guessInitialTz = () => {
  const guessedTimeZoneName = timeZoneMappingService.guessUsersTimeZone();

  if (!guessedTimeZoneName) return null;

  return createTimeZoneOption(guessedTimeZoneName);
};

const getInitialTzAndLevelValues = async (): Promise<
  [TimeZoneSelectOption | null, RemoteLevel | null]
> => {
  const locationObj = await getParentAppLocationObject();
  const searchParams = new URLSearchParams(locationObj.search);
  const {
    [TZ_NAME_KEY]: timezone,
    [LEVEL_KEY]: level,
    // todo this was init implementation for custom remote level, might be needed in future
    // [WORK_DAY_START_KEY]: wdend,
    // [WORK_DAY_END_KEY]: wdstart,
    // [WORK_DAY_START_AMPM_KEY]: wdstartampm,
    // [WORK_DAY_END_AMPM_KEY]: wdendampm,
    // [OVERLAP_KEY]: overlap,
  } = Object.fromEntries(searchParams);

  const tzFromUrl = timeZonesService.timeZonesList
    .map(createTimeZoneOption)
    .find(({ id }) => id?.toLowerCase() === timezone?.toLowerCase());

  const levelFromUrl =
    levels.find(({ level: remoteLvl }) => Number(level) === remoteLvl) ?? null;

  if (!levelFromUrl || !tzFromUrl) {
    searchParams.delete(TZ_NAME_KEY);
    searchParams.delete(LEVEL_KEY);
    setParentSearchParams(searchParams);
  }

  // todo this was init implementation for custom remote level, might be needed in future
  // if (levelFromUrl?.level === 6) {
  //   if (
  //     isNaN(Number(overlap)) ||
  //     !wdendampm ||
  //     !wdstartampm ||
  //     isNaN(Number(wdstart)) ||
  //     isNaN(Number(wdend))
  //   ) {
  //     levelFromUrl = null;
  //   } else {
  //     levelFromUrl = {
  //       ...levelFromUrl,
  //       allowedOverlap: Number(overlap),
  //       workingDayStartAmPm: {
  //         hours: Number(wdstart) as AmPmHourValues,
  //         ampm: wdstartampm as AmPmValues,
  //       },
  //       workingDayEndAmPm: {
  //         hours: Number(wdend) as AmPmHourValues,
  //         ampm: wdendampm as AmPmValues,
  //       },
  //     };
  //   }
  // }

  return [
    tzFromUrl && levelFromUrl ? tzFromUrl : guessInitialTz(),
    tzFromUrl && levelFromUrl ? levelFromUrl : null,
  ];
};

export const useTimeZoneCrossOverCalculatorState = () => {
  const initialTz = useRef<null | TimeZoneSelectOption>(null);
  const initialRemoteLevel = useRef<null | RemoteLevel>(null);

  const [selectedTz, setSelectedTz] = useState<null | TimeZoneSelectOption>(
    null
  );
  const [remoteLevel, setRemoteLevel] = useState<null | RemoteLevel>(null);

  useEffect(() => {
    getInitialTzAndLevelValues().then(([tz, remoteLvl]) => {
      initialTz.current = tz;
      initialRemoteLevel.current = remoteLvl;
      setSelectedTz(tz);
      setRemoteLevel(remoteLvl);
    });
    // todo debug why initial location is not being set
    // todo add button for copying url with tz and level values
    // todo add tz and remote levels to the url
    // todo debug why this is not always working
    // todo think about custom level and preset if one of two is missing
    // todo we need to change the script we're embedding on the webllow app (adding event listener to run a script when DOM is ready to be used)
  }, []);

  useEffect(() => {
    if (remoteLevel && selectedTz) {
      getParentAppLocationObject().then((locationObj) => {
        const searchParams = new URLSearchParams(locationObj.search);
        searchParams.set(TZ_NAME_KEY, selectedTz.id);
        searchParams.set(LEVEL_KEY, String(remoteLevel.level));

        // todo this was init implementation for custom remote level, might be needed in future
        // specific logic only for custom hours level 6
        // if (Number(remoteLevel.level) === 6) {
        //   searchParams.set(
        //     WORK_DAY_START_KEY,
        //     String(remoteLevel.workingDayStartAmPm.hours)
        //   );
        //   searchParams.set(
        //     WORK_DAY_END_KEY,
        //     String(remoteLevel.workingDayEndAmPm.hours)
        //   );
        //
        //   searchParams.set(
        //     WORK_DAY_START_AMPM_KEY,
        //     remoteLevel.workingDayStartAmPm.ampm
        //   );
        //   searchParams.set(
        //     WORK_DAY_END_AMPM_KEY,
        //     remoteLevel.workingDayEndAmPm.ampm
        //   );
        //
        //   searchParams.set(OVERLAP_KEY, String(remoteLevel.allowedOverlap));
        // } else {
        //   searchParams.delete(WORK_DAY_START_KEY);
        //   searchParams.delete(WORK_DAY_END_KEY);
        //   searchParams.delete(WORK_DAY_START_AMPM_KEY);
        //   searchParams.delete(WORK_DAY_END_AMPM_KEY);
        //   searchParams.delete(OVERLAP_KEY);
        // }

        setParentSearchParams(searchParams);
      });
    }
  }, [remoteLevel, selectedTz]);

  const autocompleteOptions = useMemo<TimeZoneSelectOption[]>(
    () =>
      timeZonesService.timeZonesList
        .map(createTimeZoneOption)
        .sort((optionA, optionB) => optionA.tzOffset - optionB.tzOffset),
    []
  );

  const overlappedTimeZonesData = useMemo<{
    // collectedTzDataWithCountries: TimeZonesWithCountriesGroupedByOffset;
    matchedTzNamesWithOffset: TimeZonesMap;
    timeZonesWithCountries: TimeZoneWithCountryData[];
  } | null>(() => {
    if (!selectedTz || !remoteLevel) return null;

    return timeZonesService.getOverlappedTimeZones({
      sourceTimeZoneName: selectedTz.id,
      selectedMinutesOverlap: remoteLevel.allowedOverlap,
      workingDayStartAmPm: remoteLevel.workingDayStartAmPm,
      workingDayEndAmPm: remoteLevel.workingDayEndAmPm,
    });
  }, [selectedTz, remoteLevel]);

  useEffect(() => {
    if (selectedTz && remoteLevel) {
      amplitudeService.logFormSubmission(selectedTz, remoteLevel);
    }
  }, [selectedTz, remoteLevel]);

  const changeSelectedTz = useCallback(
    (newValue: null | SupportedTimeZones) => {
      if (initialTz.current === null && newValue !== null)
        initialTz.current = createTimeZoneOption(newValue);

      setSelectedTz(newValue ? createTimeZoneOption(newValue) : null);
    },
    []
  );

  const onClearResults = useCallback(() => {
    changeSelectedTz(null);
    setRemoteLevel(null);
  }, []);

  const selectDefaultTz = () => setSelectedTz(initialTz.current);

  return {
    // overlappedTimeZonesWithCountries:
    //   overlappedTimeZonesData?.collectedTzDataWithCountries ?? null,
    matchedTzNamesWithOffset:
      overlappedTimeZonesData?.matchedTzNamesWithOffset ?? null,
    timeZonesWithCountries:
      overlappedTimeZonesData?.timeZonesWithCountries ?? [],
    autocompleteOptions,
    selectedTz,
    remoteLevel,
    changeSelectedTz,
    changeSelectedRemoteLevel: setRemoteLevel,
    onClearResults,
    selectDefaultTz,
    initialTz: initialTz.current,
  };
};

// todo cleanup this once we decide if we want to share custom level via url
